import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Charge, CompletedPayment, Exception, FlightDetail, ShipmentInfo } from '@cargos/sprintpay-models';
import { AwbService, StorageService, StorageTypes } from '@cargos/sprintpay-services';
import { CustomValidators } from '@cargos/sprintpay-utils';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, forkJoin, from, map, of, switchMap, take, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { FlightData } from '../models/flight';
import { SearchException } from '../models/search-exceptions';
import { SearchExceptionType } from '../models/search-exceptions-const';
import { InvalidAWBList } from '../utils/invalid-awb-list-const';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';
import { CurrentFacilityService } from './current-facility.service';
import { SummaryService } from './summary/summary.service';

@Injectable({
    providedIn: 'root',
})
export class AwbLookUpService {
    private current_payment: BehaviorSubject<ShipmentInfo | null> = new BehaviorSubject<ShipmentInfo | null>(null);
    private _awbs: BehaviorSubject<Charge[] | null> = new BehaviorSubject<Charge[] | null>(null);
    private pickupDateFormat = 'YYYY-MM-DD';
    private pickupDateAndTimeFormat = 'YYYY-MM-DD HH:mm';

    constructor(
        private awbService: AwbService,
        private storageService: StorageService,
        private cartBillService: CartBillService,
        private summaryService: SummaryService,
        private currentFacilityService: CurrentFacilityService,
        private router: Router,
        private configService: ConfigService
    ) {
        const shipment_info: ShipmentInfo | null = this.storageService.getElement(
            'shipment_info',
            StorageTypes.SESSION
        );
        if (shipment_info) {
            this.setShipmentInfo(shipment_info);
        }
        this.getConfig();
    }

    get instant_current_payment(): ShipmentInfo | null {
        return this.current_payment.value;
    }

    getCurrentPayment$(): Observable<ShipmentInfo | null> {
        return this.current_payment.asObservable();
    }

    private setShipmentInfo(shipmentInfo: ShipmentInfo | null): void {
        const isISCZero = this.configService.isISCZero();

        if (shipmentInfo?.hasOwnProperty('allowPaymentsWithoutISCPaid') && isISCZero) {
            this.configService.setAllowedPaymentsWithoutISCPaid(!!shipmentInfo?.allowPaymentsWithoutISCPaid);
        }
        if (shipmentInfo) {
            this.summaryService.setCostCenter(shipmentInfo.costCenter || '');
            this.summaryService.setSplitShipmentStatus(shipmentInfo.isSplit || false);
        }
        this.current_payment.next(shipmentInfo);
    }

    private saveShipmentInfo(shipmentInfo: ShipmentInfo | null): void {
        this.storageService.saveElement('shipment_info', shipmentInfo, StorageTypes.SESSION);
    }

    setAndSaveOnStorageShipmentInfo(shipmentInfo: ShipmentInfo | null): void {
        this.setShipmentInfo(shipmentInfo);
        this.saveShipmentInfo(shipmentInfo);
    }

    getConfig(): void {
        this.pickupDateFormat = this.configService.isEnablePickupTimeOnPickupDate()
            ? this.pickupDateAndTimeFormat
            : this.pickupDateFormat;
    }

    // TODO: Add handler exceptions
    searchAWB(
        awb: string,
        location?: string,
        validateCart = true,
        date = dayjs().format(this.pickupDateFormat)
    ): Observable<ShipmentInfo | null> {
        if (validateCart) {
            return this.isAllowedToSearch(awb).pipe(
                take(1),
                switchMap((isAllowed) => {
                    if (isAllowed) {
                        return this.getPaymentInfo(awb, location, date);
                    }

                    return of(null);
                })
            );
        }

        return this.getPaymentInfo(awb, location, date);
    }

    getPaymentInfo(awb: string, location?: string, date = dayjs().format('YYYY-MM-DD')): Observable<ShipmentInfo> {
        const facility = this.currentFacilityService.getFacilitySource();

        if (facility === 'alliance') {
            // Alliance SECTION - This is a temporal API, please implement an abstract component if BE decides is not temporal URL
            return this.awbService.awbLookUpV2(awb, date, facility, location).pipe(
                switchMap((paymentRecord: ShipmentInfo) => {
                    return this.isChargesTableAccessGranted(paymentRecord).pipe(map(() => paymentRecord));
                }),
                map((shipmentInfo: ShipmentInfo) => {
                    shipmentInfo.charges.forEach((charge: Charge, index: number) => {
                        charge.uniqueId = index.toString();
                    });

                    shipmentInfo.awb = awb;
                    shipmentInfo.date = date;
                    this.summaryService.setMAWB(awb);
                    // TODO: Remove awb and date
                    this.setAndSaveOnStorageShipmentInfo(shipmentInfo);
                    return shipmentInfo;
                })
            );
        }

        return this.awbService.awbLookUp(awb, date, facility, location).pipe(
            switchMap((paymentRecord: ShipmentInfo) => {
                return this.isChargesTableAccessGranted(paymentRecord).pipe(map(() => paymentRecord));
            }),
            map((paymentRecord: ShipmentInfo) => {
                paymentRecord.charges.forEach((charge: Charge, index: number) => {
                    charge.uniqueId = index.toString();
                    if (this.configService.isEnableFlightDateInRequest()) {
                        charge.flightDate = paymentRecord.flightDetails?.[0]?.flightDate || '';
                    }
                });

                paymentRecord.awb = awb;
                paymentRecord.date = date;
                this.summaryService.setMAWB(awb);
                // TODO: Remov awb and date
                this.setAndSaveOnStorageShipmentInfo(paymentRecord);
                return paymentRecord;
            })
        );
    }

    calculateStorage(pickupDate: string): Observable<Charge[]> {
        const facility = this.currentFacilityService.getFacilitySource();
        const charges: Charge[] = this.instant_current_payment?.charges || [];

        return this.awbService.calculateStorage(charges, pickupDate, facility);
    }

    getFacilityCode(awb: string, location: string, date = dayjs().format('YYYY-MM-DD')): Observable<string | null> {
        const facility = this.currentFacilityService.getFacilitySource();

        return this.awbService
            .awbLookUp(awb, date, facility, location)
            .pipe(map((paymentRecord: ShipmentInfo) => paymentRecord.shipmentDetails?.facilityCode || null));
    }

    getAwbsCharges$(): Observable<Charge[]> {
        return this.getCurrentPayment$().pipe(
            map((shipmentInfo) => {
                if (!shipmentInfo?.charges?.length) {
                    return [];
                }

                return this.filterChargesByAwbAndOrder(shipmentInfo.charges);
            })
        );
    }

    getHawbsCharges$(): Observable<Charge[]> {
        return this.getCurrentPayment$().pipe(
            map((shipmentInfo) => {
                if (!shipmentInfo?.charges?.length) {
                    return [];
                }

                return this.filterChargesByHawbAndOrder(shipmentInfo.charges);
            })
        );
    }

    awbContainsHAWBs$(): Observable<boolean> {
        return this.getCurrentPayment$().pipe(
            map((shipmentInfo) => {
                if (!shipmentInfo?.charges?.length) {
                    return false;
                }

                return !!this.filterChargesByHawbAndOrder(shipmentInfo.charges).length;
            })
        );
    }

    public get instant_awbs_charges(): Charge[] | null {
        return this._awbs.value;
    }

    filterChargesByAwbAndOrder(charges: Charge[]): Charge[] {
        return charges.filter((charge) => !charge.hasOwnProperty('hawbId')).sort((a, b) => a.orderId - b.orderId);
    }

    filterChargesByHawbAndOrder(charges: Charge[]): Charge[] {
        return charges
            .filter((charge) => charge.hasOwnProperty('hawbId') && charge.hawbId !== null)
            .sort((a, b) => a.orderId - b.orderId);
    }

    public get instant_hawbs_charges(): Charge[] {
        return this.filterChargesByHawbAndOrder(this.instant_current_payment?.charges || []);
    }

    getCompletedStorageAwbCompletsByFlight(flightNumber?: string, flightDate?: string): CompletedPayment[] {
        if (!this.instant_current_payment?.completedPayments?.length) {
            return [];
        }

        return this.instant_current_payment?.completedPayments?.filter(
            (paymentcompleted) =>
                !paymentcompleted.hasOwnProperty('hawb') &&
                paymentcompleted.paymentType?.toLocaleLowerCase().includes('storage') &&
                (paymentcompleted.externalData?.flightNumber || '') === (flightNumber || '') &&
                (paymentcompleted.externalData?.flightDate
                    ? dayjs(paymentcompleted.externalData?.flightDate).format('YYYY-MM-DD')
                    : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
        );
    }

    getCompletedStorageHawbPaymentsByHawbId(
        hawbId: string,
        flightNumber?: string,
        flightDate?: string
    ): CompletedPayment[] {
        if (!this.instant_current_payment?.completedPayments?.length) {
            return [];
        }

        return this.instant_current_payment?.completedPayments.filter(
            (paymentcompleted) =>
                paymentcompleted.hasOwnProperty('hawb') &&
                paymentcompleted.hawb !== null &&
                paymentcompleted.paymentType?.includes('storage') &&
                paymentcompleted.hawb === hawbId &&
                (paymentcompleted.externalData?.flightNumber || '') === (flightNumber || '') &&
                (paymentcompleted.externalData?.flightDate
                    ? dayjs(paymentcompleted.externalData?.flightDate).format('YYYY-MM-DD')
                    : '') === (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
        );
    }

    isAllowedToSearch(awb: string): Observable<boolean> {
        return of(CustomValidators.isValidAWB(awb) || InvalidAWBList.includes(awb)).pipe(
            switchMap((isValidAWB) => {
                if (isValidAWB) {
                    return of(true);
                }

                return this.isAllowedSearchInvalidAWBModal();
            }),
            switchMap((allowContinue) => {
                if (allowContinue) {
                    return this.cartBillService.isEmptyCart$().pipe(
                        switchMap((isEmpty) => {
                            if (isEmpty) {
                                return of(true);
                            }

                            return this.isAllowedMultipleAWBs();
                        })
                    );
                }

                return of(allowContinue);
            })
        );
    }

    isAllowedToAddCharges(): Observable<boolean> {
        return this.cartBillService.isEmptyCart$().pipe(
            switchMap((emptyCart) => {
                if (emptyCart) {
                    return of(true);
                }
                return this.isAllowedMultipleAWBs().pipe(take(1));
            })
        );
    }

    isAllowedToAddShipment(): Observable<boolean> {
        return of(this.configService.getMaxItemsPerCart()).pipe(
            switchMap((maxItemsPerCart) => {
                if (maxItemsPerCart) {
                    return this.cartBillService.getLengthCart$().pipe(
                        map((lengthCart) => {
                            return lengthCart + 1 <= maxItemsPerCart;
                        })
                    );
                }
                return of(true);
            })
        );
    }

    isAllowedMultipleAWBs(): Observable<boolean> {
        return of(this.configService.isAllowedMultipleAWBs()).pipe(
            switchMap((isAllowed) => {
                if (!isAllowed) {
                    return from(
                        Swal.fire({
                            title: 'You already have payment in your cart',
                            text: `Do you want to start again?`,
                            icon: 'warning',
                            cancelButtonText: 'New search',
                            confirmButtonText: 'Go to cart',
                            showCancelButton: true,
                            showConfirmButton: true,
                            showCloseButton: true,
                            customClass: {
                                cancelButton: 'order-1',
                                confirmButton: 'order-2',
                            },
                        })
                    ).pipe(
                        switchMap((result) => {
                            if (result.dismiss === Swal.DismissReason.cancel) {
                                return this.cartBillService.deleteCart(false).pipe(map(() => true));
                            }

                            if (result.isConfirmed) {
                                this.router.navigate(['/cart']);
                            }

                            return of(false);
                        })
                    );
                }

                return of(true);
            })
        );
    }

    isAllowedSearchInvalidAWBModal(): Observable<boolean> {
        return of(this.configService.isAllowedSearchInvalidAWB()).pipe(
            switchMap((isAllowed) => {
                if (isAllowed) {
                    return from(
                        Swal.fire({
                            title: 'Oops!',
                            text: 'The AWB entered is not a valid AWB number, please confirm to continue.',
                            icon: 'warning',
                            allowOutsideClick: false,
                            showCancelButton: true,
                            customClass: {
                                cancelButton: 'btn link',
                            },
                            confirmButtonText: 'Confirm',
                            cancelButtonText: 'Cancel',
                            showCloseButton: false,
                            reverseButtons: true,
                            allowEscapeKey: false,
                        })
                    ).pipe(map((result) => result.isConfirmed));
                }

                return throwError(() => SearchException.fromJson({ code: SearchExceptionType.INVALID_AWB }));
            })
        );
    }

    isChargesTableAccessGranted(shipmentInfo: ShipmentInfo): Observable<boolean> {
        return forkJoin([
            this.isAllowedContinueWithAWBNotFound(shipmentInfo.charges).pipe(take(1)),
            this.isAllowedPayWithoutShipmentInformation(shipmentInfo).pipe(take(1)),
        ]).pipe(map((validations) => validations.every((validation) => validation === true)));
    }

    isAllowedContinueWithAWBNotFound(charges: Charge[]): Observable<boolean> {
        return of(this.configService.isAllowedContinueWithAWBNotFound() || !!charges?.length).pipe(
            switchMap((isAllowed) => {
                if (isAllowed) {
                    return of(true);
                }

                return throwError(() => this.getAWBNotFoundException());
            })
        );
    }

    isAllowedPayWithoutShipmentInformation(shipmentInfo: ShipmentInfo): Observable<boolean> {
        return of(
            this.configService.isAllowedPayWithoutShipmentInformation() ||
                (!!shipmentInfo.shipmentDetails?.origin && shipmentInfo.flightDetails?.length)
        ).pipe(
            switchMap((isAllowed) => {
                if (isAllowed) {
                    return of(true);
                }

                return throwError(() => this.getAWBNotFoundException());
            })
        );
    }

    getAWBNotFoundException(): Exception {
        return Exception.fromJson({
            description:
                'We could not find the AWB you entered. Please contact your airport representative for further details. AWBs will only show up after they have been manifested and departed from port of loading.',
            type: 'warning',
            code: 'AWB_NOT_FOUND',
        });
    }

    findNotificationDetail(status: string): FlightDetail | undefined {
        return this.instant_current_payment?.flightDetails?.find((flight) => {
            return flight?.notificationDetails.find((notification) => notification.statusCode === status);
        });
    }

    isAWBReceivedFromFlight(): boolean {
        return !!this.findNotificationDetail('RCF');
    }

    isAWBReceivedFromShipper(): boolean {
        return !!this.findNotificationDetail('RCS');
    }

    isAWBDelivered(): boolean {
        return !!this.findNotificationDetail('DLV');
    }

    isAWBAgentNotifiedOfArrival(): boolean {
        return !!this.findNotificationDetail('NFD');
    }

    getFlightNumbersAndFlightDatesOnFlightDetails(): FlightData[] {
        return (
            this.instant_current_payment?.flightDetails
                .map((flight) => {
                    return {
                        flightNumber: flight?.flightNumber,
                        flightDate: flight?.flightDate,
                    };
                })
                .sort((a, b) => this.sortByDate(a.flightDate, b.flightDate)) || []
        );
    }

    private sortByDate(date: string, dateTwo: string): 1 | -1 | 0 {
        const a = new Date(date).getTime();
        const b = new Date(dateTwo).getTime();

        if (a > b) {
            return 1;
        }
        if (a < b) {
            return -1;
        }
        return 0;
    }

    getAwbsChargesByFlightNumberAndFlightDate$(flightNumber: string, flightDate: string): Observable<Charge[]> {
        return this.getAwbsCharges$().pipe(
            map((awbsCharges: Charge[]) => {
                return awbsCharges.filter(
                    (charge: Charge) =>
                        charge.flightNumber === flightNumber &&
                        (charge.flightDate ? dayjs(charge.flightDate).format('YYYY-MM-DD') : '') ===
                            (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                );
            })
        );
    }

    getHawbsChargesByFlightNumber$(flightNumber: string, flightDate: string): Observable<Charge[]> {
        return this.getHawbsCharges$().pipe(
            map((awbsCharges: Charge[]) => {
                return awbsCharges.filter(
                    (charge: Charge) =>
                        charge.flightNumber === flightNumber &&
                        (charge.flightDate ? dayjs(charge.flightDate).format('YYYY-MM-DD') : '') ===
                            (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                );
            })
        );
    }

    getCharges$(): Observable<Charge[]> {
        return this.getCurrentPayment$().pipe(
            map((shipmentInfo: ShipmentInfo | null) => {
                if (!shipmentInfo?.charges?.length) {
                    return [];
                }

                return shipmentInfo.charges;
            })
        );
    }

    getChargeByUniqueId(uniqueId: string): Observable<Charge | undefined> {
        return this.getCharges$().pipe(map((charges) => charges.find((charge) => charge.uniqueId === uniqueId)));
    }

    getFlights$(): Observable<FlightDetail[]> {
        return this.getCurrentPayment$().pipe(
            map((shipmentInfo) => {
                return shipmentInfo?.flightDetails || [];
            })
        );
    }

    getFlightByFlightNumber$(flightNumber: string): Observable<FlightDetail | undefined> {
        return this.getFlights$().pipe(
            map((flights) => {
                return flights.find((flight) => {
                    return flight.flightNumber === flightNumber;
                });
            })
        );
    }

    getFlightByFlightNumberAndFlightDate$(
        flightNumber: string,
        flightDate: string
    ): Observable<FlightDetail | undefined> {
        return this.getFlights$().pipe(
            map((flights) => {
                return flights.find((flight) => {
                    return (
                        (flight.flightNumber || '') === (flightNumber || '') &&
                        (flight.flightDate ? dayjs(flight.flightDate).format('YYYY-MM-DD') : '') ===
                            (flightDate ? dayjs(flightDate).format('YYYY-MM-DD') : '')
                    );
                });
            })
        );
    }

    getAwbsFromShipmentInfo(): string | undefined {
        return this.instant_current_payment?.awb;
    }

    updateCharges(charges: Charge[]): void {
        const shipment_info = this.instant_current_payment;
        if (shipment_info) {
            shipment_info.charges = charges;
        }
        this.current_payment.next(shipment_info);
    }

    getShipmentArrivalDate$(): Observable<string | undefined> {
        return this.getCurrentPayment$().pipe(map((shipment) => shipment?.shipmentDetails?.arrivalDate));
    }

    getFlightDate(): string {
        const flightDetails = this.instant_current_payment?.flightDetails;
        return flightDetails?.[0]?.flightDate || '';
    }
}
